/* Law of Ambition: lightweight projected 3D pyramid. No external dependencies. */
(()=>{
  function init(){
    const root=document.getElementById('loa-unified');
    if(!root)return;
    const canvas=root.querySelector('[data-ambition-canvas]');
    const stage=root.querySelector('[data-pyramid-stage]');
    if(!canvas||!stage)return;
    const ctx=canvas.getContext('2d',{alpha:true});
    if(!ctx)return;
    if(root.loaPyramid&&typeof root.loaPyramid.destroy==='function')root.loaPyramid.destroy();
    const reduceQuery=matchMedia('(prefers-reduced-motion:reduce)');
    const baseAngle=.47,listeners=[];
    let width=0,height=0,angle=baseAngle,targetAngle=baseAngle;
    let selected=0,opened=false,openAmount=0,layerAmount=[0,0,0,0],raf=0,lastTime=0;
    let inView=true,dragging=false,lastX=0,startX=0,startY=0,dragDistance=0,horizontalDrag=false,destroyed=false,pagePaused=false;
    let camera,right,up,forward,scale,cx,cy,resizeObserver,intersectionObserver,mutationObserver;
    const faces=[];let meshOpacity=1;
    const markers=[1,2,3,4].map(layer=>root.querySelector('[data-layer-marker="'+layer+'"]'));
    root.dataset.pyramidOpen='false';
    const originalTouchAction=canvas.style.touchAction,originalCursor=canvas.style.cursor;
    canvas.style.touchAction='pan-y';canvas.style.cursor='grab';
    const dot=(a,b)=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
    const subtract=(a,b)=>a.map((v,i)=>v-b[i]);
    const cross=(a,b)=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
    const normal=a=>{const m=Math.hypot(...a)||1;return a.map(v=>v/m)};
    function listen(target,type,handler,options){target.addEventListener(type,handler,options);listeners.push(()=>target.removeEventListener(type,handler,options))}
    function active(){return !destroyed&&!pagePaused&&!document.hidden&&inView&&root.isConnected&&!stage.closest('[hidden]')&&width>0&&height>0}
    function setCamera(){
      // The open workbook map stays facing the visitor; closed sculpture orbits freely.
      const viewAngle=angle*(1-openAmount)+(.13+Math.sin(angle)*.11)*openAmount;
      camera=[Math.sin(viewAngle)*8.6,4.2-openAmount*.5,Math.cos(viewAngle)*8.6];
      forward=normal(subtract([0,1.45+openAmount*.35,0],camera));
      right=normal(cross(forward,[0,1,0]));up=cross(right,forward);
      cx=width*.50;cy=height*.51;
      let fit=Infinity;
      // Fit actual sculpture vertices, retaining the approved scale at normal aspects.
      const bounds=[[0,3.82,0]];
      [[1.97,-.12],[1.97,.06],[2.12,.25],[1.72,.08],[1.22,.89],[1.20,.93],[.79,1.73],[.77,1.78],[1.05,2.06],[.36,2.58],[.34,2.63]].forEach(([size,y])=>{
        for(const x of [-size,size])for(const z of [-size,size])bounds.push([x,y,z]);
      });
      for(let i=0;i<24;i++){const a=i/24*Math.PI*2;bounds.push([Math.cos(a)*2.19,-.07,Math.sin(a)*2.19])}
      for(const p of bounds){
        const v=subtract(p,camera),depth=dot(v,forward);
        const px=dot(v,right)/depth,py=-dot(v,up)/depth;
        if(px>0)fit=Math.min(fit,(width*.94-cx)/px);
        if(px<0)fit=Math.min(fit,(cx-width*.06)/-px);
        if(py>0)fit=Math.min(fit,(height*.93-cy)/py);
        if(py<0)fit=Math.min(fit,(cy-height*.06)/-py);
      }
      const closedScale=Math.min(width*1.60,height*1.62,fit);
      // The open map is a front-facing set of triangular prisms. Fit those
      // actual vertices rather than reserving space for the closed sculpture.
      let minX=Infinity,maxX=-Infinity,minY=Infinity,maxY=-Infinity;
      for(let i=0;i<4;i++)for(const p of mapPiece(i))for(const z of [p[2],p[2]-.22]){
        const v=subtract([p[0],p[1],z],camera),depth=dot(v,forward);
        const px=dot(v,right)/depth,py=-dot(v,up)/depth;
        minX=Math.min(minX,px);maxX=Math.max(maxX,px);minY=Math.min(minY,py);maxY=Math.max(maxY,py);
      }
      const openScale=Math.min(width*.86/(maxX-minX),height*.85/(maxY-minY));
      const openX=width*.5-(minX+maxX)*.5*openScale,openY=height*.5-(minY+maxY)*.5*openScale;
      scale=closedScale+(openScale-closedScale)*openAmount;
      cx+=(openX-cx)*openAmount;cy+=(openY-cy)*openAmount;
    }
    function project(p){const v=subtract(p,camera),z=dot(v,forward);return{x:cx+dot(v,right)*scale/z,y:cy-dot(v,up)*scale/z,z}}
    function path(points){ctx.beginPath();points.forEach((p,i)=>{const q=project(p);i?ctx.lineTo(q.x,q.y):ctx.moveTo(q.x,q.y)});ctx.closePath()}
    function face(points,color,tier){
      const center=points[0].map((_,i)=>points.reduce((s,p)=>s+p[i],0)/points.length);
      faces.push({points,color,tier,opacity:meshOpacity,z:project(center).z});
    }
    function mix(hex,amount){
      const rgb=[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)),red=[116,31,38];
      return 'rgb('+rgb.map((v,i)=>Math.round(v+(red[i]-v)*amount)).join(',')+')';
    }
    function pull(tier){const distance=tier?layerAmount[tier-1]*openAmount*.23:0;return[Math.sin(angle)*distance,Math.cos(angle)*distance]}
    function frustum(y,b,t,h,tier){
      const shift=pull(tier);
      const v=[[-b,y,-b],[b,y,-b],[b,y,b],[-b,y,b],[-t,y+h,-t],[t,y+h,-t],[t,y+h,t],[-t,y+h,t]].map(p=>[p[0]+shift[0],p[1],p[2]+shift[1]]);
      [['#3c3d40',0,1,5,4],['#8b8b89',1,2,6,5],['#26272a',2,3,7,6],['#5b5c5e',3,0,4,7],['#bebdb8',4,5,6,7]].forEach(f=>face(f.slice(1).map(i=>v[i]),f[0],tier));
    }
    function pyramid(y,b,h,tier){
      const shift=pull(tier),v=[[-b,y,-b],[b,y,-b],[b,y,b],[-b,y,b]].map(p=>[p[0]+shift[0],p[1],p[2]+shift[1]]),apex=[shift[0],y+h,shift[1]];
      ['#424347','#a3a29e','#2e2f33','#646568'].forEach((color,i)=>face([v[i],v[(i+1)%4],apex],color,tier));
    }
    const mapTriangles=[
      [[0,3.60],[-.94,2.00],[.94,2.00]],
      [[-.94,2.00],[-1.88,.40],[0,.40]],
      [[-.94,2.00],[.94,2.00],[0,.40]],
      [[.94,2.00],[0,.40],[1.88,.40]]
    ];
    const mapShifts=[[0,.15],[-.15,-.10],[0,0],[.15,-.10]];
    function mapPiece(index){
      const points=mapTriangles[index],center=[points.reduce((sum,p)=>sum+p[0],0)/3,points.reduce((sum,p)=>sum+p[1],0)/3];
      const shift=mapShifts[index],z=.13+layerAmount[index]*.24;
      return points.map(p=>[center[0]+(p[0]-center[0])*.90+shift[0],center[1]+(p[1]-center[1])*.90+shift[1],z]);
    }
    function triangularPrism(index){
      const front=mapPiece(index),back=front.map(p=>[p[0],p[1],p[2]-.22]),tier=index+1;
      face(back,'#26272a',tier);
      ['#46474b','#b6b5ae','#5b5c60'].forEach((color,i)=>face([front[i],front[(i+1)%3],back[(i+1)%3],back[i]],color,tier));
      face(front,'#92928e',tier);
    }
    function line(a,b,color,weight=1){const p=project(a),q=project(b);ctx.beginPath();ctx.moveTo(p.x,p.y);ctx.lineTo(q.x,q.y);ctx.strokeStyle=color;ctx.lineWidth=weight;ctx.stroke()}
    function draw(){
      if(!active())return;
      setCamera();ctx.clearRect(0,0,width,height);
      const atmosphere=ctx.createRadialGradient(cx,cy-height*.04,0,cx,cy-height*.04,width*.51);
      atmosphere.addColorStop(0,'rgba(190,188,181,.075)');atmosphere.addColorStop(.55,'rgba(190,188,181,.025)');atmosphere.addColorStop(1,'rgba(190,188,181,0)');
      ctx.fillStyle=atmosphere;ctx.fillRect(0,0,width,height);
      for(let i=-6;i<=6;i++){
        line([i*1.15,-.10,-5.5],[i*1.15,-.10,5.5],'rgba(180,179,173,.055)',.65);
        line([-6.9,-.10,i*1.15],[6.9,-.10,i*1.15],'rgba(180,179,173,.055)',.65);
      }
      const floor=project([0,-.04,0]),radius=scale*.22;
      ctx.save();ctx.translate(floor.x,floor.y+5);ctx.scale(1,.24);
      const shadow=ctx.createRadialGradient(0,0,0,0,0,radius);
      shadow.addColorStop(0,'rgba(0,0,0,.58)');shadow.addColorStop(.6,'rgba(0,0,0,.19)');shadow.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=shadow;ctx.fillRect(-radius,-radius,radius*2,radius*2);ctx.restore();
      const ring=[];for(let i=0;i<=100;i++){const a=i/100*Math.PI*2;ring.push([Math.cos(a)*2.19,-.07,Math.sin(a)*2.19])}
      path(ring);ctx.strokeStyle='rgba(189,186,180,.18)';ctx.lineWidth=.75;ctx.stroke();
      faces.length=0;
      meshOpacity=1-openAmount;
      if(meshOpacity>.001){
        frustum(-.08,1.97,1.97,.14,0);frustum(.08,1.72,1.22,.81,0);
        frustum(.93,1.20,.79,.8,0);frustum(1.78,.77,.36,.8,0);pyramid(2.63,.34,.73,0);
      }
      meshOpacity=openAmount;if(meshOpacity>.001)for(let i=0;i<4;i++)triangularPrism(i);
      faces.sort((a,b)=>b.z-a.z).forEach(f=>{
        ctx.save();ctx.globalAlpha=f.opacity;
        const emphasis=f.tier?layerAmount[f.tier-1]:0;
        path(f.points);ctx.fillStyle=mix(f.color,emphasis*.27);ctx.fill();
        ctx.strokeStyle=emphasis>.005?'rgba(181,72,79,'+(.25+emphasis*.35)+')':'rgba(227,224,217,.25)';
        ctx.lineWidth=emphasis>.1?1:.65;ctx.stroke();
        ctx.save();path(f.points);ctx.clip();
        const sheen=ctx.createLinearGradient(cx-width*.31,0,cx+width*.28,height);
        sheen.addColorStop(0,'rgba(241,238,230,0)');sheen.addColorStop(.39,'rgba(241,238,230,.12)');sheen.addColorStop(.40,'rgba(241,238,230,.015)');sheen.addColorStop(.72,'rgba(241,238,230,.055)');sheen.addColorStop(1,'rgba(241,238,230,0)');
        ctx.fillStyle=sheen;ctx.fillRect(0,0,width,height);ctx.restore();ctx.restore();
      });
      if(selected&&openAmount>.02){
        const points=mapPiece(selected-1),center=points[0].map((_,i)=>points.reduce((sum,p)=>sum+p[i],0)/3);
        const p=project(center),glow=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,Math.min(width,height)*.2);
        glow.addColorStop(0,'rgba(153,31,43,.12)');glow.addColorStop(1,'rgba(153,31,43,0)');ctx.fillStyle=glow;ctx.fillRect(0,0,width,height);
      }
      placeMarkers();
    }
    function placeMarkers(){
      if(openAmount<.02)return;
      const stageRect=stage.getBoundingClientRect();
      markers.forEach((marker,index)=>{
        if(!marker)return;
        const points=mapPiece(index),center=points[0].map((_,i)=>points.reduce((sum,p)=>sum+p[i],0)/3),p=project(center);
        const markerX=p.x,markerY=p.y;
        const parent=marker.offsetParent,parentRect=parent&&parent!==stage?parent.getBoundingClientRect():stageRect;
        marker.style.left=(stageRect.left-parentRect.left+markerX)+'px';
        marker.style.top=(stageRect.top-parentRect.top+markerY)+'px';
      });
    }
    function stop(){if(raf)cancelAnimationFrame(raf);raf=0;lastTime=0}
    function frame(now){
      raf=0;if(!active()){lastTime=0;return}
      const delta=lastTime?Math.min(40,now-lastTime):16;lastTime=now;
      const ease=reduceQuery.matches?1:1-Math.exp(-delta/115);
      angle+=(targetAngle-angle)*ease;
      let moving=Math.abs(targetAngle-angle)>.0001;
      const openTarget=opened?1:0;openAmount+=(openTarget-openAmount)*ease;
      if(Math.abs(openTarget-openAmount)>.001)moving=true;else openAmount=openTarget;
      layerAmount=layerAmount.map((v,i)=>{const target=selected===i+1?1:0,next=v+(target-v)*ease;if(Math.abs(target-next)>.001)moving=true;return Math.abs(target-next)<.001?target:next});
      draw();if(moving)raf=requestAnimationFrame(frame);else lastTime=0;
    }
    function wake(){if(active()&&!raf){lastTime=0;raf=requestAnimationFrame(frame)}}
    function emitLayer(){root.dispatchEvent(new CustomEvent('loa:layer',{detail:{layer:selected,open:opened}}))}
    function changeOpen(next){
      if(opened===next)return false;
      opened=next;root.dataset.pyramidOpen=String(opened);
      root.dispatchEvent(new CustomEvent('loa:open',{detail:{open:opened}}));return true;
    }
    function setOpen(value){
      if(destroyed)return;
      const next=Boolean(value),changed=changeOpen(next);
      if(next){if(!selected)selected=1}else selected=0;
      wake();if(changed)emitLayer();
    }
    function setLayer(value){
      if(destroyed)return;
      const next=Math.max(1,Math.min(4,Math.round(Number(value)||1)));
      changeOpen(true);
      selected=next;wake();emitLayer();
    }
    function reset(){setOpen(false);targetAngle=baseAngle;wake()}
    function destroy(){
      if(destroyed)return;destroyed=true;stop();dragging=false;
      if(resizeObserver)resizeObserver.disconnect();if(intersectionObserver)intersectionObserver.disconnect();if(mutationObserver)mutationObserver.disconnect();
      listeners.splice(0).forEach(remove=>remove());
      canvas.style.touchAction=originalTouchAction;canvas.style.cursor=originalCursor;
      if(root.loaPyramid===api)delete root.loaPyramid;
    }
    const api={setLayer,setOpen,reset,destroy};root.loaPyramid=api;
    root.querySelectorAll('[data-orbit]').forEach(button=>listen(button,'click',()=>{
      const direction=Number(button.dataset.orbit)<0?-1:1;
      const next=selected?((selected-1+direction+4)%4)+1:(direction>0?1:4);
      targetAngle+=direction*.13;setLayer(next);
    }));
    function pick(event){
      if(!active()||!faces.length)return;
      const rect=canvas.getBoundingClientRect();if(!rect.width||!rect.height)return;
      const x=(event.clientX-rect.left)*width/rect.width,y=(event.clientY-rect.top)*height/rect.height;
      for(let i=faces.length-1;i>=0;i--){
        const f=faces[i];if(f.opacity<.5)continue;
        const points=f.points.map(project);let inside=false;
        for(let p=0,q=points.length-1;p<points.length;q=p++){
          const a=points[p],b=points[q];if((a.y>y)!==(b.y>y)&&x<(b.x-a.x)*(y-a.y)/(b.y-a.y)+a.x)inside=!inside;
        }
        if(inside){
          if(f.tier)setLayer(f.tier);else setOpen(true);return;
        }
      }
    }
    listen(canvas,'pointerdown',event=>{
      if(!active()||!event.isPrimary||(event.pointerType==='mouse'&&event.button!==0))return;
      dragging=true;horizontalDrag=false;dragDistance=0;startX=lastX=event.clientX;startY=event.clientY;targetAngle=angle;
      canvas.style.cursor='grabbing';
      try{canvas.setPointerCapture(event.pointerId)}catch(error){}
    });
    listen(canvas,'pointermove',event=>{
      if(!dragging||!event.isPrimary)return;
      const dx=event.clientX-startX,dy=event.clientY-startY;
      if(!horizontalDrag){
        if(Math.abs(dy)>10&&Math.abs(dy)>Math.abs(dx)){dragging=false;canvas.style.cursor='grab';return}
        if(Math.abs(dx)<10)return;
        horizontalDrag=true;setOpen(true);
      }
      const distance=event.clientX-lastX;dragDistance+=Math.abs(distance);
      targetAngle+=distance*.008;lastX=event.clientX;
      if(reduceQuery.matches)angle=targetAngle;wake();
    });
    listen(canvas,'pointerup',event=>{canvas.style.cursor='grab';if(!dragging)return;dragging=false;if(!horizontalDrag&&dragDistance<6)pick(event);wake()});
    listen(canvas,'pointercancel',()=>{dragging=false;canvas.style.cursor='grab'});listen(canvas,'lostpointercapture',()=>{dragging=false;canvas.style.cursor='grab'});
    function resize(){
      if(destroyed)return;
      const rect=stage.getBoundingClientRect();
      if(rect.width<1||rect.height<1){width=0;stop();return}
      width=rect.width;height=rect.height;
      // Keep Retina detail while capping backing-store work on very large screens.
      const dpr=Math.min(devicePixelRatio||1,2,Math.sqrt(4200000/(width*height)));
      const pixelWidth=Math.max(1,Math.round(width*dpr)),pixelHeight=Math.max(1,Math.round(height*dpr));
      if(canvas.width!==pixelWidth||canvas.height!==pixelHeight){canvas.width=pixelWidth;canvas.height=pixelHeight}
      ctx.setTransform(pixelWidth/width,0,0,pixelHeight/height,0,0);wake();
    }
    resizeObserver=new ResizeObserver(resize);resizeObserver.observe(stage);
    if('IntersectionObserver'in window){
      intersectionObserver=new IntersectionObserver(entries=>{inView=entries[0].isIntersecting;if(inView)wake();else stop()},{threshold:0});intersectionObserver.observe(stage);
    }
    mutationObserver=new MutationObserver(()=>{if(stage.closest('[hidden]')){stop();return}resize()});
    for(let ancestor=stage;ancestor;ancestor=ancestor.parentElement)mutationObserver.observe(ancestor,{attributes:true,attributeFilter:['hidden','class','style']});
    listen(document,'visibilitychange',()=>{if(document.hidden)stop();else resize()});
    listen(window,'pagehide',event=>{pagePaused=true;stop();if(!event.persisted)destroy()});
    listen(window,'pageshow',()=>{pagePaused=false;resize()});
    listen(window,'resize',resize,{passive:true});
    listen(reduceQuery,'change',wake);resize();
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
})();
